import type { particles } from 'app/contracts/Particles';
import type { ParticlesStage } from 'app/views/home/webgl/particles/scenes/ParticlesStage';
import type { ParticleCore } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { ScriptList } from 'app/views/home/webgl/particlesOptimised/core/scripts';
import type { DepartmentScene, HomeWebGLSizes } from 'app/views/home/webgl/webgl.types';

/**
 * Shared plumbing for the particles-optimised department scenes (stage 4).
 *
 * The reference departments read a shared sceneProps bag; here the facade
 * owns one mutable HomeTickContext (same shared-identity pattern) and
 * refreshes it once per tick BEFORE routing tick() to the active department
 * — so departments never recompute per-frame scalars.
 */
export interface HomeTickContext {
	/** HomeWebGLFrame.containerX (sceneProps.progress.containerX). */
	containerX: number;
	/** HomeWebGLFrame.progress (sceneProps.progress.ratio). */
	progress: number;
	/**
	 * The icon container's world x this frame — the reference departments'
	 * _positionParticleContainer value (containerX +
	 * zonePositions.homeParticles.cx * container.w), which doubles as
	 * sceneProps.mouse.particleContainersX.home: the homeMouse repell target
	 * subtracts it from the raw mouse cords. NaN-guarded to 0 by the facade
	 * until zonePositions exist.
	 */
	homeContainerX: number;
	/** window.deltaTime as read once by the facade this tick. */
	deltaTime: number;
}

/** Constructor dependencies for the optimised departments. */
export interface DepartmentDeps {
	core: ParticleCore;
	scripts: ScriptList;
	/** The REUSED particles-type stage (icon container positioning). */
	stage: ParticlesStage;
	/** Latest sizes snapshot (the facade's _sizes — live identity). */
	getSizes(): HomeWebGLSizes;
	/** Facade-owned per-tick scalars (shared identity, updated every tick). */
	context: HomeTickContext;
	/**
	 * The timeline's SHARED scene-state bag (deps.getSceneProps() — identity
	 * stable). Departments use the same runtime seams the reference ones do:
	 * WRITE mouse.particleContainersX/Y for their zone (MouseTarget /
	 * ParticleCords / the physics realX read it), and hand it to the reused
	 * HomeScreens module (stage 5).
	 */
	sceneProps: particles.SharedSceneContext;
	/**
	 * ParticleBehaviourLookup.pause()/unpause() equivalent: while paused the
	 * facade skips the kernels, the script list and the uploads (per-particle
	 * state freezes) but keeps routing department ticks — exactly the
	 * reference split (HomeField.tick runs during pause, particle ticks
	 * don't). The screens department pulses this across its start beat.
	 */
	setKernelsPaused(paused: boolean): void;
	/**
	 * The host's upward colourise callbacks (HomeWebGLDeps — the same seam
	 * the reference DepartmentColours reaches via HomeField.webglDeps). The
	 * spaces/systems departments' colour cycles tint the big timeline items
	 * through these (stage 6).
	 */
	colouriseSpacesItems(colourSet: Array<number>, screenColour?: number | string, floorColour?: any): void;
	colouriseSystemsItems(colourSet: Array<number | string>, instant: boolean): void;
}

/**
 * What the facade (ParticlesOptimisedWebGL) needs from its systems department,
 * beyond the DepartmentScene surface: the passive-tick `isActive` gate, the
 * physics lerp-completion routing, and teardown. Typing the facade's
 * `_systemsDepartment` as this interface (rather than the concrete
 * SystemsDepartment) is the seam that lets the 'shaders' type substitute a GPU
 * systems department via `_createSystemsDepartment` — a non-behavioural change
 * for the default type (SystemsDepartment already satisfies it).
 */
export interface SystemsDepartmentLike extends DepartmentScene {
	readonly isActive: boolean;
	onPhysicsTargetReached(lane: number): void;
	destroy(): void;
}

/**
 * '#rrggbb' / '#rgb' colour string (HomeColours sets) to the 0xRRGGBB number
 * the tint lanes store. The reference hands these strings straight to
 * sprite.tint (Pixi parses CSS colours); the SoA lanes are numeric.
 */
export function hexToTint(hex: string): number {
	let h = hex.charAt(0) === '#' ? hex.slice(1) : hex;

	if (h.length === 3) {
		h = h.charAt(0) + h.charAt(0) + h.charAt(1) + h.charAt(1) + h.charAt(2) + h.charAt(2);
	}

	return parseInt(h, 16);
}

/**
 * HomeParticle.random(base, seed) for the argument shapes the intro/icon
 * processors actually use (no minus flags): base + Math.random() * seed.
 */
export function particleRandom(base: number, seed: number): number {
	return base + Math.random() * seed;
}
